/**
 * PATH: packages/ui/src/diagnostics/clientErrorReporterGlobalListeners.ts
 * FILE_ID: SHARED-DIAGNOSTICS-LISTENERS-003
 * PURPOSE: Install isolated browser error and keyboard listeners.
 * WHY: Keeps listener lifecycle separate from reporter transport state.
 * FLOW: browser event → supplied recorder/flush callback → diagnostics buffer.
 * RELATED: clientErrorReporter.ts.
 */
export interface AutomaticDiagnostic {
  event_type: 'unhandled_error' | 'unhandled_rejection'
  message: string
  stack: string
  extra?: Record<string, unknown>
}

export interface GlobalListenerArgs {
  record: (diagnostic: AutomaticDiagnostic) => void
  flush: (extra?: { keepalive?: boolean }) => Promise<number>
}

export function installClientErrorGlobalListeners({ record, flush }: GlobalListenerArgs): void {
  if (typeof window === 'undefined') return
  window.addEventListener('error', (event) => {
    const error = event.error
    record({
      event_type: 'unhandled_error',
      message: error instanceof Error ? error.message : String(event.message ?? 'unknown'),
      stack: error instanceof Error ? error.stack ?? '' : '',
      extra: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    })
  })
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason
    record({
      event_type: 'unhandled_rejection',
      message: reason instanceof Error ? reason.message : String(reason ?? 'unknown'),
      stack: reason instanceof Error ? reason.stack ?? '' : '',
    })
  })
  window.addEventListener('beforeunload', () => {
    flush({ keepalive: true }).catch(() => {})
  })
}

export function installClientDiagnosticsKeyboardShortcut(
  getBuffer: () => readonly unknown[],
): void {
  if (typeof window === 'undefined') return
  window.addEventListener('keydown', (event) => {
    const isModifier = event.metaKey || event.ctrlKey
    if (!isModifier || !event.shiftKey || event.key.toLowerCase() !== 'd') return

    event.preventDefault()
    const dump = JSON.stringify(getBuffer(), null, 2)
    try {
      if (navigator?.clipboard?.writeText) {
        navigator.clipboard.writeText(dump).catch(() => {})
      }
    } catch {
      /* clipboard unavailable */
    }
    // eslint-disable-next-line no-console
    console.log(
      '%c[Quotech diagnostics] Copied %d events to clipboard',
      'color:#0f766e;font-weight:600',
      getBuffer().length,
    )
    // eslint-disable-next-line no-console
    console.log(dump)
    showToast(`Copied ${getBuffer().length} diagnostic events to clipboard`)
  })
}

function showToast(text: string): void {
  if (typeof document === 'undefined') return
  const id = 'quotech-diag-toast'
  let element = document.getElementById(id)
  if (!element) {
    element = document.createElement('div')
    element.id = id
    element.style.cssText = `
      position:fixed;bottom:16px;right:16px;z-index:99999;
      background:#0f766e;color:#fff;padding:8px 14px;border-radius:8px;
      font:13px/1.4 system-ui,-apple-system,sans-serif;
      box-shadow:0 4px 12px rgba(0,0,0,.18);
      pointer-events:none;opacity:0;transition:opacity .15s ease-out;
    `
    document.body.appendChild(element)
  }
  element.textContent = text
  element.style.opacity = '1'
  window.setTimeout(() => {
    if (element) element.style.opacity = '0'
  }, 2200)
}
